// ────────────────────────────────────────────────────────────────────
// The right rail — the flow, and who is being waited on.
//
// ONE DEPARTURE FROM THE DRAWING, and it stands.
//
//   • `FLOW · LAST 24H` is headed `FLOW · IN THE QUEUE NOW`. GET /api/queue
//     returns a snapshot of what is standing in each stage right now; nothing
//     anywhere records WHEN a job entered a stage, so no twenty-four-hour
//     window over the pipeline exists to be read. (`POST /{id}/advance` began
//     writing a stage event on 2026-09-01, but every job that has not moved
//     since carries none — a windowed count over that data would read zero for
//     most of the pipeline and call it flow.) Printing "LAST 24H" over a
//     snapshot asserts something the data cannot support, which is the precise
//     failure this screen's no-composition rule exists to stop. The counts are
//     real; only the window is unavailable. All six stages are here now,
//     numbered from the wire, empty ones included.
//
// `ALERTS` IS drawn, and it reads the real alert feed — GET /api/notifications,
// the same per-user source the header bell has always shown. Every line is
// `message_md`, composed server-side by `notify()`, printed verbatim; the dot
// is the server's `severity`. What that feed does and does not produce today is
// recorded in QueueScreen.tsx.
//
// The rail narrows with the rest of the screen: its stage counts are counted
// off the rows currently rendered, so filtering by a client shows that
// client's flow rather than leaving a whole-tenant figure standing beside a
// narrowed list. ALERTS does not narrow — it is not a view of the queue's rows
// and has no stage or client to be narrowed by.
// ────────────────────────────────────────────────────────────────────
import type { AppNotification } from "@/lib/types";
import type { FlowStage } from "./queueModel";
import type { QueueRow } from "./types";

const MICRO = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.09em",
  textTransform: "uppercase" as const, color: "var(--ink-3)",
};

// The severity dot, one entry per value the server sends (`info | watch |
// warn | crit`). STYLE FRAGMENTS, not bare colour strings: a token that
// travels as a naked string can be spread onto `color` by a later edit and no
// search for a colour property would ever find it — the exact shape that has
// escaped three sweeps. Held this way the token cannot leave its fill.
const SEVERITY_DOT: Record<string, { background: string }> = {
  crit:  { background: "var(--err)" },
  warn:  { background: "var(--warn)" },
  watch: { background: "var(--accent)" },
  info:  { background: "var(--info)" },
};

// An unrecognised severity falls back to the neutral tone rather than being
// dropped: an alert nobody can see is worse than an uncoloured one.
const DOT_BASE = {
  flex: "none" as const, marginTop: 5, width: 7, height: 7, borderRadius: 999,
};

export function QueueRail(
  { flow, blocked, alerts, alertsUnavailable }: {
    flow: FlowStage[];
    blocked: QueueRow[];
    alerts: AppNotification[];
    /** The alert feed is a SECOND call, so it has its own failure. A rail that
     *  silently drew "no alerts" over a failed fetch would report an outage as
     *  calm. */
    alertsUnavailable: boolean;
  },
) {
  return (
    <aside
      data-testid="queue-rail"
      style={{
        borderLeft: "1px solid var(--hairline)", background: "var(--surface-2)",
        padding: "16px 12px", display: "flex", flexDirection: "column", gap: 20,
      }}
    >
      <section data-testid="queue-flow">
        <div style={MICRO}>Flow · in the queue now</div>
        <div style={{ marginTop: 6 }}>
          {flow.map((s) => (
            <div
              key={s.stage}
              data-testid={`queue-flow-${s.stage}`}
              style={{
                display: "flex", alignItems: "baseline", gap: 8,
                padding: "4px 0", borderBottom: "1px solid var(--line-2)",
              }}
            >
              <span
                style={{
                  flex: "none", width: 15, height: 15, borderRadius: 4,
                  display: "grid", placeItems: "center", background: "var(--line-2)",
                  fontFamily: "var(--mono)", fontSize: 9, color: "var(--ink-3)",
                }}
              >
                {s.index}
              </span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--ink-2)" }}>
                {s.label}
              </span>
              <span style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-3)" }}>
                {s.count}
              </span>
            </div>
          ))}
          {flow.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>no jobs in flight</div>
          )}
        </div>
      </section>

      <section data-testid="queue-blocked">
        <div style={MICRO}>Blocked · needs someone else</div>
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
          {blocked.map((r) => (
            <article
              key={r.id}
              data-testid={`queue-blocked-${r.id}`}
              style={{
                padding: "8px 9px", borderRadius: 3,
                border: "1px solid var(--hairline)", background: "var(--surface-alert)",
              }}
            >
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-2)" }}>
                {r.ref}
              </div>
              <div style={{ marginTop: 3, fontSize: 12, color: "var(--ink-2)" }}>
                {/* WHO — the server's own answer. The side (client / staff) is
                    the wire's too; neither is inferred from the status here. */}
                waiting on {r.blocked_on}
              </div>
              <div style={{ ...MICRO, letterSpacing: "0.06em", marginTop: 3 }}>
                {/* FOR HOW LONG. Null when the block was never recorded as an
                    event: the server refuses to read a duration off updated_at,
                    which moves on any edit, so the honest reading is that it is
                    not known — not a number quietly rounded from the wrong
                    column. */}
                {r.blocked_days === null ? "since not recorded" : `${r.blocked_days} d`}
              </div>
            </article>
          ))}
          {blocked.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>nothing blocked</div>
          )}
        </div>
      </section>

      <section data-testid="queue-alerts">
        <div style={MICRO}>Alerts</div>
        <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 8 }}>
          {alerts.map((a) => (
            <div
              key={a.id}
              data-testid={`queue-alert-${a.id}`}
              style={{ display: "flex", gap: 7, alignItems: "flex-start" }}
            >
              <span
                aria-hidden
                style={{ ...DOT_BASE, ...(SEVERITY_DOT[a.severity] ?? SEVERITY_DOT.info) }}
              />
              <div style={{ minWidth: 0 }}>
                {/* The alert's own sentence, composed by server/notify.py and
                    printed. The browser neither phrases it nor decides what
                    counts as an alert. */}
                <div
                  data-testid={`queue-alert-text-${a.id}`}
                  style={{ fontSize: 12, color: "var(--ink-2)" }}
                >
                  {a.message_md}
                </div>
                <div style={{ ...MICRO, letterSpacing: "0.06em", marginTop: 2 }}>
                  {/* WHEN, as the stamp the server sent — the same treatment
                      the header bell gives it. Not "2h ago": a relative phrase
                      would be the browser composing one. */}
                  {a.created_at.replace("T", " ").slice(0, 16)}
                </div>
              </div>
            </div>
          ))}
          {alertsUnavailable && (
            <div data-testid="queue-alerts-unavailable"
                 style={{ fontSize: 12, color: "var(--ink-3)" }}>
              alerts could not be loaded
            </div>
          )}
          {!alertsUnavailable && alerts.length === 0 && (
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>nothing to flag</div>
          )}
        </div>
      </section>
    </aside>
  );
}
